
import { api } from '../core';
import { Plan } from '../../types';
import { MOCK_PLANS } from '../mockData';
import { localDB } from '../helpers';

const mapToUI = (apiPlan: any): Plan => {
    // Parse features from CSV string or array
    const features = typeof apiPlan.description === 'string' 
        ? apiPlan.description.split(',').map((s:string) => s.trim()) 
        : [];

    return {
        id: apiPlan.id,
        name: apiPlan.name,
        price: apiPlan.price.toString(),
        period: apiPlan.billingPeriod || 'monthly',
        features: features.length > 0 ? features : ['Standard Features'],
        buttonText: 'Subscribe',
        highlight: apiPlan.price > 0 && apiPlan.price < 100, // Heuristic
        icon: 'fa-cube', // Default, maybe map from name
        color: apiPlan.price === 0 ? 'slate' : 'emerald',
        limits: {
            messagesPerDay: apiPlan.maxMessagesPerMonth ? Math.floor(apiPlan.maxMessagesPerMonth / 30) : 10,
            agentsCount: apiPlan.maxAgentsPerSpace || 1,
            flowsCount: apiPlan.maxFlowsPerSpace || 0,
            servicesPerDay: apiPlan.maxServiceCallsPerMonth ? Math.floor(apiPlan.maxServiceCallsPerMonth / 30) : 0,
            allowFlows: apiPlan.hasApiAccess,
            allowProServices: apiPlan.hasCustomModels
        }
    };
};

export const PlansService = {
  getAll: async (): Promise<Plan[]> => {
    return api.execute<Plan[]>(
      '/api/V1/Plan/GetAllByLanguage',
      async () => localDB.get<Plan[]>('lahja_plans', MOCK_PLANS),
      { mockDelay: 300 }
    ).then(res => {
        if (Array.isArray(res) && res.length > 0 && 'billingPeriod' in res[0]) {
            return res.map(mapToUI);
        }
        return res as Plan[];
    });
  },

  getById: async (id: string): Promise<Plan | undefined> => {
    return api.execute<Plan | undefined>(
      `/api/V1/Plan/Get/${id}`,
      async () => localDB.get<Plan[]>('lahja_plans', MOCK_PLANS).find(p => p.id === id)
    ).then(res => (res && 'billingPeriod' in res ? mapToUI(res) : res as Plan));
  },

  create: async (plan: Plan): Promise<Plan> => {
    return api.execute<Plan>(
      '/api/V1/Plan/Create',
      async () => {
        localDB.updateItem('lahja_plans', plan, MOCK_PLANS);
        return plan;
      },
      { method: 'POST', body: {
          name: plan.name,
          price: parseFloat(plan.price),
          billingPeriod: plan.period,
          description: plan.features.join(', '),
          isActive: true
      } }
    ).then(res => ('billingPeriod' in (res as any) ? mapToUI(res) : res));
  },

  update: async (plan: Plan): Promise<Plan> => {
    return api.execute<Plan>(
      `/api/V1/Plan/Update/${plan.id}`,
      async () => {
        localDB.updateItem('lahja_plans', plan, MOCK_PLANS);
        return plan;
      },
      { method: 'PUT', body: {
          id: plan.id,
          body: {
            name: plan.name,
            price: parseFloat(plan.price),
            billingPeriod: plan.period,
            description: plan.features.join(', ')
          }
      } }
    ).then(res => ('billingPeriod' in (res as any) ? mapToUI(res) : res));
  },

  delete: async (id: string): Promise<boolean> => {
    return api.execute<boolean>(
      `/api/V1/Plan/Delete/${id}`,
      async () => {
        localDB.deleteItem('lahja_plans', id, MOCK_PLANS);
        return true;
      },
      { method: 'DELETE' }
    );
  }
};
